import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Input,
  Select,
} from "@win-reality/react-ui";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const playerFormSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  hit_side: z.enum(["left", "right", "switch"]),
  pitch_side: z.enum(["left", "right"]),
  jerseyNumber: z.union([z.string(), z.number()]).optional(),
});

const hitSideItems = [
  { label: "Left", value: "left" },
  { label: "Right", value: "right" },
  { label: "Switch", value: "switch" },
];

const pitchSideItems = [
  { label: "Left", value: "left" },
  { label: "Right", value: "right" },
];

export interface PlayerEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: Record<string, unknown>;
  onSubmit: (data: Record<string, unknown>) => void;
  showJerseyNumber?: boolean;
  /** Dialog theme; defaults to the Dialog's own default */
  variant?: "light" | "dark";
}

export default function PlayerEditModal({
  open,
  onOpenChange,
  defaultValues,
  onSubmit,
  showJerseyNumber = false,
  variant,
}: PlayerEditModalProps) {
  const form = useForm<z.infer<typeof playerFormSchema>>({
    resolver: zodResolver(playerFormSchema),
    defaultValues: {
      ...defaultValues,
    },
  });

  const handleSubmit = async (data: z.infer<typeof playerFormSchema>) => {
    await onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onOpenChange(false)}
      defaultCancelButton={false}
      size="md"
      variant={variant}
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>Edit Player</DialogTitle>
            </DialogHeader>

            <Form {...form}>
              <form
                id="player-edit-form"
                onSubmit={form.handleSubmit(handleSubmit)}
                className="grid gap-4 py-4"
              >
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>First Name:</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="First Name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Last Name:</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Last Name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="hit_side"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Bats:</FormLabel>
                      <FormControl>
                        <Select
                          items={hitSideItems}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder="Select Bats"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pitch_side"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Throws:</FormLabel>
                      <FormControl>
                        <Select
                          items={pitchSideItems}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder="Select Throws"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {showJerseyNumber && (
                  <FormField
                    control={form.control}
                    name="jerseyNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Jersey Number:</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            placeholder="Jersey Number"
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </form>
            </Form>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                type="submit"
                form="player-edit-form"
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
